<template>
  <a
    :href="isLoading ? null : job.url"
    target="_blank"
    class="result-item"
    :style="resultItemElStyles"
  >
    <div class="info">
      <h3 class="title" :title="job.title">
        <span v-if="isLoading" class="loading-indicator -title" />
        <span v-else>{{ job.title }}</span>

        <IconArrow width="24" height="24" />
      </h3>
      <div v-if="isLoading" class="loading-indicator -locations" />
      <div v-else>
        <ul v-if="showLocations" class="locations">
          <li
            v-for="(location, index) in resolvedLocations"
            :key="index"
            class="location"
          >
            {{ location }}
            <span
              v-if="index < resolvedLocations.length - 1"
              class="separator"
            />
          </li>
        </ul>
      </div>

      <div class="details">
        <div
          v-for="(detail, index) in shownDetails"
          :key="detail.key"
          class="detail"
          :data-testid="`detail-${detail.key}`"
        >
          <div v-if="isLoading" class="loading-indicator -detail" />
          <div v-else>
            <ul v-if="detailIsArray(detail.key)" class="detail-items">
              <li
                v-for="(detailItem, index) in job[detail.key]"
                :key="index"
                class="detail-item"
              >
                {{ detailItem }}
                <span v-if="index < job[detail.key].length - 1">,</span>
              </li>
            </ul>

            <span v-else>{{ job[detail.key] }}</span>
            <span v-if="index < shownDetails.length - 1" class="separator" />
          </div>
        </div>
      </div>
    </div>
    <div class="actions">
      <IconArrow width="24" height="24" />
    </div>
  </a>
</template>

<script>
import IconArrow from "../../Icons/IconArrow.vue";
import jobSearchGlobal from "../../../globalSettingProps/jobSearchDefaultGlobalProp.js";
import exampleResultItem from "./exampleResultItemProps.js";

export default {
  name: "ResultItem",
  components: {
    IconArrow,
  },
  props: {
    jobSearchGlobal: {
      type: Object,
      required: true,
      default() {
        return jobSearchGlobal;
      },
    },
    item: {
      type: Object,
      required: true,
      default() {
        return exampleResultItem;
      },
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    job() {
      return this.item;
    },
    showLocations() {
      return this.jobSearchGlobal.config.filters.options.some(
        (filter) => filter.key === "location" && filter.visibleOnCards === true,
      );
    },
    resolvedLocations() {
      const { showFullAddress } = this.jobSearchGlobal.config.results;
      const { nationwideText } = this.jobSearchGlobal.config.filters;

      const jobLocations = showFullAddress
        ? this.job.full_address
        : this.job.location;

      // Could be undefined if field is not provided by typesense
      if (this.job.location_count === undefined) {
        return jobLocations;
      }

      return this.job.location_count > 0 ? jobLocations : [nationwideText];
    },
    shownDetails() {
      return this.jobSearchGlobal.config.filters.options.filter(
        (filter) => filter.visibleOnCards === true && filter.key !== "location",
      );
    },
    resultItemElStyles() {
      const { corners, accentColor } = this.jobSearchGlobal.config.general;

      return {
        "--result-item-border-radius": corners.value === "round" ? "10px" : 0,
        "--search-filter-accent-color": accentColor.value,
      };
    },
  },
  methods: {
    detailIsArray(key) {
      return Array.isArray(this.job[key]);
    },
  },
};
</script>

<style lang="scss" scoped>
@include reset;

.result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 15px 20px;

  color: var(--search-filter-text-color);
  text-decoration: none;

  background-color: var(--search-filter-card-bg-color);
  border-radius: var(--result-item-border-radius);

  &:hover .title {
    color: var(--search-filter-accent-color);

    :deep(svg) {
      transform: rotate(45deg);
      color: var(--search-filter-accent-color);
      opacity: 1;
    }
  }

  @include mobile {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
}

.info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;

  width: 100%;
}

.title {
  @include font-family("heading");
  @include scale-fonts("heading-5");

  display: flex;
  align-self: stretch;
  justify-content: space-between;
  transition: all 250ms ease-in-out;

  :deep(svg) {
    transform-origin: center;

    width: auto;

    color: var(--search-filter-text-color);

    opacity: 0.3;

    transition: all 250ms ease-in-out;

    @include mobile {
      display: none;
    }
  }

  @include mobile {
    font-size: 21px;
    color: var(--search-filter-text-color);
  }
}

.locations {
  @include reset-list;

  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
  justify-content: flex-start;

  @include mobile {
    flex-direction: column;
    align-items: flex-start;
  }
}

.location {
  @include font-family("body");
  @include scale-fonts("body-small");

  display: flex;
  gap: 5px;
  align-items: center;

  .separator {
    display: inline-flex;
    align-self: center;

    width: 4px;
    height: 4px;

    background-color: var(--search-filter-text-color, #1d1d1f);
    border-radius: 50%;

    @include mobile {
      display: none;
    }
  }
}

.details {
  @include font-family("body");
  @include scale-fonts("body-small");

  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;

  .detail {
    display: flex;
    gap: 6px;
    align-items: center;
    width: 100%;

    @include mobile {
      gap: 3px;
    }
  }

  .detail-items {
    @include reset-list;

    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    justify-content: flex-start;

    @include mobile {
      display: block;
    }
  }

  .detail-item {
    display: flex;
    align-items: center;
    color: var(--search-filter-text-secondary-color);
  }
}

.actions {
  display: none;

  @include mobile {
    display: flex;
    gap: 10px;
    align-self: stretch;
    justify-content: flex-end;

    :deep(svg) {
      transform: rotate(45deg);
      width: auto;
      color: var(--search-filter-accent-color);
      opacity: 1;
    }
  }
}

.loading-indicator {
  align-self: stretch;
  height: 19px;
  background-color: #d9d9d9;
  border-radius: 5px;

  &.-title {
    width: 50%;
    height: 32px;
  }

  &.-locations {
    width: 30%;
  }

  &.-detail {
    width: 150px;
  }
}
</style>
